/**
 * SPECIALISTS — the review pipeline as accordion rows (YAG-440).
 *
 * Replaces the modal's rail-and-detail two-pane with rows that expand in
 * place: a two-pane rail does not fit a vertical section. Every honesty rule
 * carries over from the modal it replaces: recorded states only, dropped
 * findings stay visible struck and dimmed, refuted survivors say so in
 * verification's words, and a row with nothing to show gets no chevron.
 */
import { useState } from "react";
import type { CodeReviewReport, FindingVerification, ReviewFinding } from "@yagni/shared";
import { Finding } from "../../ui/index.js";
import {
  DETAIL_NO_FINDINGS,
  VERIFICATION_MARK_COPY,
  followUpOutcomeLine,
  notCarriedLine,
} from "../../workers/copy.js";
import {
  buildPipeline,
  countClassifications,
  countPinned,
  decisionFor,
  diffCheckMark,
  rosterNote,
  type PipelineEntry,
} from "./pipeline.js";
import type { RefinementDecision } from "@yagni/shared";

function FindingRow({
  finding,
  decision,
  verification,
}: {
  finding: ReviewFinding;
  decision: RefinementDecision | null;
  /** Set on the verification stage's rows only (YAG-419). */
  verification?: FindingVerification | null;
}) {
  const dropped = decision?.decision === "dropped";
  // A refuted survivor is struck like an editor drop, with its own words —
  // it survived the editor and then failed against the actual checkout.
  const refuted = verification?.verdict === "refuted" && verification.failed !== true;
  const verificationMark =
    verification && verification.failed !== true
      ? (VERIFICATION_MARK_COPY[verification.verdict] ?? null)
      : null;
  // Carried is the norm and says nothing; only the strike needs words.
  const mark = dropped && decision ? (
    notCarriedLine(decision)
  ) : verificationMark ? (
    `${verificationMark}${verification?.note ? ` · ${verification.note}` : ""}`
  ) : null;
  return (
    <Finding
      severity={finding.severity}
      confidence={finding.confidence}
      file={finding.file}
      line={finding.line}
      body={finding.body}
      mark={mark}
      dimmed={dropped || refuted}
    />
  );
}

/** One specialist's record, expanded beneath its row. */
function SpecialistDetail({
  entry,
  report,
}: {
  entry: PipelineEntry;
  report: CodeReviewReport;
}) {
  const decisions = report.refinementDecisions;
  // Verification rows mark each survivor with its verdict (YAG-419).
  const verificationFor = (findingId: string): FindingVerification | null =>
    entry.key === "verification"
      ? (decisions?.verifications?.find((v) => v.id === findingId) ?? null)
      : null;

  if (entry.result?.entries && entry.result.entries.length > 0) {
    const outcome = followUpOutcomeLine(
      countClassifications(entry.result.entries),
      countPinned(entry.result.entries),
    );
    return (
      <div className="rvc-roster-detail" data-testid={`rvc-stage-${entry.key}`}>
        {outcome ? (
          <p className="rvc-roster-outcome" data-testid="rvc-follow-up-outcome">
            {outcome}
          </p>
        ) : null}
        <ul className="wrk-review-findings">
          {entry.result.entries.map((row, index) => (
            <Finding
              key={row.key ?? index}
              severity={row.severity}
              check={row.role}
              file={row.path}
              line={row.line ?? null}
              body={row.note?.trim() ? row.note : (row.key ?? "prior comment")}
              mark={diffCheckMark(row, null)}
              dimmed={row.classification === "resolved"}
            />
          ))}
        </ul>
      </div>
    );
  }

  if (entry.result?.findings && entry.result.findings.length > 0) {
    return (
      <div className="rvc-roster-detail" data-testid={`rvc-stage-${entry.key}`}>
        <ul className="wrk-review-findings">
          {entry.result.findings
            .map((finding) => ({
              finding,
              decision: decisionFor(decisions, entry.key, finding.id),
              verification: verificationFor(finding.id),
            }))
            // Carried first, dropped after (stable within each group): the
            // reader wants what survived before the audit trail of cuts.
            .sort(
              (a, b) =>
                (a.decision?.decision === "dropped" ? 1 : 0) -
                (b.decision?.decision === "dropped" ? 1 : 0),
            )
            .map(({ finding, decision, verification }) => (
              <FindingRow
                key={finding.id}
                finding={finding}
                decision={decision}
                verification={verification}
              />
            ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="rvc-roster-detail" data-testid={`rvc-stage-${entry.key}`}>
      <p className="rvc-roster-outcome">{DETAIL_NO_FINDINGS}</p>
    </div>
  );
}

/** Does expanding this row show anything the note doesn't already say? */
function hasDetail(entry: PipelineEntry): boolean {
  return (
    (entry.result?.findings?.length ?? 0) > 0 ||
    (entry.result?.entries?.length ?? 0) > 0
  );
}

export function SpecialistsRoster({ report }: { report: CodeReviewReport }) {
  const pipeline = buildPipeline(report);
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <ul className="rvc-roster" data-testid="rvc-roster">
      {pipeline.map((entry) => {
        const expandable = hasDetail(entry);
        const open = openKey === entry.key;
        return (
          <li key={entry.key}>
            <button
              type="button"
              className="rvc-roster-row"
              disabled={!expandable}
              aria-expanded={expandable ? open : undefined}
              data-testid={`rvc-roster-${entry.key}`}
              data-state={entry.state}
              onClick={() => setOpenKey(open ? null : entry.key)}
            >
              <span className={`rvc-roster-dot is-${entry.state}`} aria-hidden />
              <span className="rvc-roster-name">{entry.label}</span>
              <span className="rvc-roster-note">{rosterNote(entry, report)}</span>
              {expandable ? (
                <span className="rvc-roster-tri" aria-hidden>
                  {open ? "▾" : "▸"}
                </span>
              ) : null}
            </button>
            {open && expandable ? <SpecialistDetail entry={entry} report={report} /> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** "7 ran · 1 failed · 2 skipped" for the closed section row. Counts only what
 * is recorded. Failed checks stopped counting as "ran" in YAG-490 — a check
 * that produced no review must not swell the ran count; nor does one that
 * never read the code. A live run with no
 * recorded pass yet says "preparing" — the honest word for the checkout and
 * packet window, where no specialist has started. */
export function specialistsSummary(report: CodeReviewReport): string | null {
  const pipeline = buildPipeline(report);
  const unread = pipeline.filter(
    (entry) => entry.state === "done" && entry.result?.unread === true,
  ).length;
  const ran = pipeline.filter((entry) => entry.state === "done").length - unread;
  const failed = pipeline.filter((entry) => entry.state === "failed").length;
  const skipped = pipeline.filter((entry) => entry.state === "skipped").length;
  const live = pipeline.some(
    (entry) => entry.state === "running" || entry.state === "waiting",
  );
  const preparing =
    report.status === "in_progress" &&
    !("deep" in report.stages) &&
    !("diff_check" in report.stages);
  const parts: string[] = [];
  if (ran > 0) parts.push(`${ran} ran`);
  if (failed > 0) parts.push(`${failed} failed`);
  if (unread > 0) parts.push(`${unread} did not read the code`);
  if (skipped > 0) parts.push(`${skipped} skipped`);
  if (live) parts.push(preparing ? "preparing" : "reviewing now");
  return parts.length > 0 ? parts.join(" · ") : null;
}
